import React, { useMemo, useState } from 'react';
import { useElementSize } from '@fullstory/react-hooks';
import { defaultSpacing, ChartSpacing } from '../../util/spacing';
import css from './index.module.scss';

interface RenderParams {
  width: number;
  height: number;
  overlay: HTMLDivElement | null;
}

interface ChartProps {
  width?: number;
  height: number;
  children: (params: RenderParams) => React.ReactNode;
  spacing?: ChartSpacing;
  includeOverlay?: boolean;
}

export const Chart: React.FC<ChartProps> = ({
  width: propWidth,
  height,
  children,
  spacing = defaultSpacing,
  includeOverlay = true,
}) => {
  const [overlay, setOverlay] = useState<HTMLDivElement | null>(null);
  const { elementSize, ref } = useElementSize();
  const width = useMemo(() => {
    if (propWidth !== undefined) return propWidth;
    if (elementSize) return elementSize.width;
    return 0;
  }, [elementSize, propWidth]);
  const innerWidth = width - (spacing.right + spacing.left);
  const innerHeight = height - (spacing.top + spacing.bottom);

  return (
    <div data-component="Chart" className={css.searchieChart} ref={ref}>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
        <g transform={`translate(${spacing.left},${spacing.top})`}>
          {
            // one frame can be rendered with a spacing, wait until the div has a postive size before rendering
            innerWidth >= 0 && children({ width: innerWidth, height: innerHeight, overlay })
          }
        </g>
      </svg>
      {includeOverlay && (
        <div
          ref={setOverlay}
          style={{
            position: 'absolute',
            top: spacing.top,
            left: spacing.left,
            width: innerWidth,
            height: innerHeight,
          }}
        />
      )}
    </div>
  );
};
